import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  Release,
  RolloutCampaign,
  CreateReleaseRequest,
  CreateCampaignRequest,
} from '@hospasense/contracts';
import {
  fetchReleases,
  publishRelease,
  createRelease,
  fetchRolloutCampaigns,
  createCampaign,
} from '../lib/queries-releases.js';
import { CreateReleaseModal, CreateCampaignModal } from './release-modals.js';
import { useAuth } from '../lib/auth.js';
import {
  Button,
  Card,
  CardBody,
  PageHeader,
  Badge,
  StatusPill,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../components/ui/index.js';

export function ReleasesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('release.manage');
  const canRollout = hasPermission('release.rollout');

  const [releases, setReleases] = useState<Release[]>([]);
  const [campaigns, setCampaigns] = useState<RolloutCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isCreateReleaseOpen, setIsCreateReleaseOpen] = useState(false);
  const [isCreateCampaignOpen, setIsCreateCampaignOpen] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [releasesData, campaignsData] = await Promise.all([
        fetchReleases(),
        fetchRolloutCampaigns(),
      ]);
      setReleases(releasesData);
      setCampaigns(campaignsData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, []);

  const handleCreateRelease = async (data: CreateReleaseRequest) => {
    await createRelease(data);
    await loadAll();
  };

  const handlePublishRelease = async (id: string) => {
    await publishRelease(id);
    await loadAll();
  };

  const handleCreateCampaign = async (data: CreateCampaignRequest) => {
    await createCampaign(data);
    await loadAll();
  };

  return (
    <div className="space-y-8" data-testid="releases-page">
      <PageHeader
        title="Releases & Rollout Campaigns"
        subtitle="Manage software versions, artifacts, and progressive ring deployments with automated health gates."
        actions={
          <div className="flex items-center gap-2">
            {canManage && (
              <Button
                variant="secondary"
                data-testid="create-release-button"
                onClick={() => setIsCreateReleaseOpen(true)}
              >
                Create Release Draft
              </Button>
            )}
            {canRollout && (
              <Button
                variant="primary"
                data-testid="launch-campaign-button"
                onClick={() => setIsCreateCampaignOpen(true)}
              >
                Launch Rollout Campaign
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <ErrorState
          description={error}
          onRetry={() => {
            void loadAll();
          }}
        />
      )}

      {/* Active Campaigns Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-text-primary">Active Rollout Campaigns</h2>
        {loading ? (
          <LoadingState label="Loading campaigns..." />
        ) : campaigns.length === 0 ? (
          <EmptyState
            title="No active rollout campaigns in progress"
            description="Launch a new rollout campaign to progressively deploy published releases across rings."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {campaigns.map((camp) => (
              <Card key={camp.id}>
                <CardBody>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-mono text-xs font-semibold text-text-muted">
                        {camp.releaseVersion}
                      </span>
                      <h3 className="text-base font-bold text-text-primary">{camp.name}</h3>
                    </div>
                    <Badge
                      tone={
                        camp.status === 'in_progress'
                          ? 'info'
                          : camp.status === 'completed'
                            ? 'success'
                            : camp.status === 'paused'
                              ? 'warning'
                              : 'error'
                      }
                    >
                      {camp.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-xs text-text-secondary">
                    <span>
                      Target Ring:{' '}
                      <strong className="uppercase text-text-primary">{camp.targetRing}</strong>
                    </span>
                    <span className="tabular-nums">
                      Progress: {camp.updatedInstallationsCount} / {camp.targetInstallationsCount}{' '}
                      nodes
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-bg-s1">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{
                        width: `${
                          camp.targetInstallationsCount > 0
                            ? (camp.updatedInstallationsCount / camp.targetInstallationsCount) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>

                  {camp.pausedReason && (
                    <div className="mt-3 rounded-lg bg-warning-bg p-2 text-xs text-warning">
                      Paused: {camp.pausedReason}
                    </div>
                  )}
                  {camp.rollbackReason && (
                    <div className="mt-3 rounded-lg bg-error-bg p-2 text-xs text-error">
                      Rolled back: {camp.rollbackReason}
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <Link to={`/releases/campaigns/${camp.id}`}>
                      <Button size="sm" variant="outline" data-testid="manage-rollout-link">
                        Manage Rollout →
                      </Button>
                    </Link>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Release Catalogue Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-text-primary">Release Catalogue</h2>
        <TableContainer>
          <Table density="compact">
            <THead>
              <TR>
                <TH>Version</TH>
                <TH>Title</TH>
                <TH>Channel</TH>
                <TH>Status</TH>
                <TH>Min Compatible</TH>
                <TH align="right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {releases.map((rel) => (
                <TR key={rel.id}>
                  <TD className="font-mono">{rel.version}</TD>
                  <TD>
                    <div className="font-semibold text-text-primary">{rel.title}</div>
                    <div className="text-xs text-text-muted">
                      {rel.releaseNotes.slice(0, 75)}...
                    </div>
                  </TD>
                  <TD>
                    <Badge tone="neutral">{rel.channel}</Badge>
                  </TD>
                  <TD>
                    <StatusPill
                      tone={
                        rel.status === 'published'
                          ? 'success'
                          : rel.status === 'draft'
                            ? 'warning'
                            : 'neutral'
                      }
                      label={rel.status}
                    />
                  </TD>
                  <TD className="font-mono">{rel.minCompatibleVersion}</TD>
                  <TD align="right">
                    {rel.status === 'draft' && canManage && (
                      <Button
                        size="sm"
                        variant="primary"
                        data-testid="publish-release-btn"
                        onClick={() => handlePublishRelease(rel.id)}
                      >
                        Publish
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      </div>

      <CreateReleaseModal
        isOpen={isCreateReleaseOpen}
        onClose={() => setIsCreateReleaseOpen(false)}
        onSubmit={handleCreateRelease}
      />

      <CreateCampaignModal
        isOpen={isCreateCampaignOpen}
        releases={releases}
        onClose={() => setIsCreateCampaignOpen(false)}
        onSubmit={handleCreateCampaign}
      />
    </div>
  );
}
